import type { FeatureCollection, Point } from 'geojson';
import type { ExpressionSpecification, FilterSpecification, MapMouseEvent } from 'mapbox-gl';
import { ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Layer, Marker, Source } from 'react-map-gl/mapbox';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { COLOR_NEUTRAL_WHITE, COLOR_TEXT_PRIMARY, FONT_SIZE_100 } from '@alltrails/denali/tokens';
import type { WaypointCategoryUid } from '@alltrails/types/Waypoint';
import { DEFAULT_WAYPOINT_CATEGORY_UID, WAYPOINT_CATEGORY_UIDS } from '@alltrails/waypoints/utils/waypointCategories';
import useMap from '../../hooks/useMap';
import { defaultPOIPinLayerLayoutProps, defaultSymbolLayerLayoutProps, getLayerBeforeId, getWaypointActiveLayerId } from '../../utils/layers';
import createWaypointPinSvg, {
  createAllCategoryWaypointPinSvgs,
  createCategoryWaypointPinSvg,
  getWaypointImageName
} from '../../utils/svgDefinitions/createWaypointPinSvg';
import { poiPinHeightMd, poiPinIconWidthMd, poiPinVerticalOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import useSvgImages from '../../utils/useSvgImages';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import styles from './WaypointPinLayer.module.scss';

export type WaypointFeatureProperty = {
  id?: string;
  categoryUid?: string;
  /** Property used for the labels-layer text. Only relevant when labels are rendered. */
  title?: string;
};

// Defaults for GeoJSON callers (UserWaypoints / CustomRouteWaypoints) whose features carry these
// property names verbatim. Vector tile callers (CommunityWaypoints) override these to match the
// backend's schema (e.g. `waypoint_id`, `category_uid`, `name`).
const DEFAULT_PROPERTIES: Required<WaypointFeatureProperty> = {
  id: 'id',
  categoryUid: 'categoryUid',
  title: 'title'
};

/** Each feature in `featureCollection` should carry these properties (GeoJSON callers only). */
export type WaypointPinFeatureProperties = {
  id: number;
  title?: string;
  categoryUid: WaypointCategoryUid;
};

export type WaypointMarkerProperties = {
  id: number;
  latitude: number;
  longitude: number;
  /** Rendered as a label below the marker. Caller decides what to display (e.g. waypoint name with a fallback). */
  title?: ReactNode;
  categoryUid?: WaypointCategoryUid;
};

type GeoJSONSourceConfig = {
  type: 'geojson';
  data: FeatureCollection<Point>;
};

type VectorSourceConfig = {
  type: 'vector';
  tiles: string[];
  sourceLayer: string;
};

type WaypointPinLayerProps = {
  /** Source config — `geojson` for client-built feature collections, `vector` for tiled layers. */
  source: GeoJSONSourceConfig | VectorSourceConfig;
  /**
   * SVG color variant — `'user'` for the viewer's own waypoints, `'community'` for everyone else's.
   * Determines which set of category-icon SVGs is registered with the map.
   */
  variant: 'user' | 'community';
  /**
   * Id of the waypoint that should render as enlarged ("active"). Composed by the caller from
   * hover + selection state. The layer compares this against `properties.id` in a Mapbox expression,
   * so updating it re-renders without a source-data change.
   */
  activeWaypointId?: number;
  /** Feature-property name overrides — defaults to GeoJSON case (`id`, `categoryUid`, `title`). */
  properties?: WaypointFeatureProperty;
  /** Optional Mapbox filter expression (e.g. category filter on vector-tile sources). */
  filter?: FilterSpecification;
  /**
   * When provided, a draggable `<Marker>` overlay is rendered on top of the source layer at this
   * position. Used for in-edit drag UI (UserWaypoints' edit draft, CustomRouteWaypoints' sidebar
   * selection).
   */
  waypointMarkerProperties?: WaypointMarkerProperties;
  isDraggable?: boolean;
  onSelectedDragEnd?: (event: { lngLat: { lng: number; lat: number } }) => void;
  /**
   * Per-pin labels rendering policy.
   * - `'always'` (default) — render labels for every feature (filtered by `text-field` non-null + collisions).
   * - `'never'` — omit the labels layer entirely.
   * - `'when-active'` — render the label only for the currently active waypoint (hover/selected).
   */
  labelVisibility?: 'always' | 'never' | 'when-active';
  /** Distinct GeoJSON/vector source id — required so multiple instances of this layer can coexist on the same map. */
  sourceId: string;
  /** Distinct Mapbox layer id — also used to register event handlers via `MapLayerInteractionsContext`. */
  layerId: string;
  minZoom?: number;
  onMouseMove?: (event: MapMouseEvent) => void;
  onMouseLeave?: () => void;
  onClick?: (event: MapMouseEvent) => void;
};

const WaypointPinLayer = ({
  source,
  variant,
  activeWaypointId,
  properties,
  filter,
  waypointMarkerProperties,
  isDraggable = false,
  onSelectedDragEnd,
  labelVisibility = 'always',
  minZoom,
  sourceId,
  layerId,
  onMouseMove,
  onMouseLeave,
  onClick
}: WaypointPinLayerProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const map = useMap();

  // Merge caller overrides over the GeoJSON-case defaults so callers that don't pass `properties`
  // (UserWaypoints, CustomRouteWaypoints) read the canonical feature fields.
  const waypointProperties = { ...DEFAULT_PROPERTIES, ...properties };

  // Memoize these in the component instead of defining as constants since they need access to the 'window' object.
  const activeSvg = useMemo(() => createWaypointPinSvg('md', variant), [variant]);
  const inactiveSvg = useMemo(() => createWaypointPinSvg('xs', variant), [variant]);
  const categorySvgDefinitions = useMemo(() => createAllCategoryWaypointPinSvgs(intl, variant), [intl, variant]);
  const svgDefinitions = useMemo(() => [activeSvg, inactiveSvg, ...categorySvgDefinitions], [activeSvg, inactiveSvg, categorySvgDefinitions]);
  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const activeLayerId = getWaypointActiveLayerId(layerId);

  useEffect(() => {
    if (!isMobile) {
      if (onMouseMove) {
        registerEventHandler(layerId, 'mousemove', onMouseMove);
        registerEventHandler(activeLayerId, 'mousemove', onMouseMove);
      }
      if (onMouseLeave) {
        registerEventHandler(layerId, 'mouseleave', onMouseLeave);
        registerEventHandler(activeLayerId, 'mouseleave', onMouseLeave);
      }
    }

    if (onClick) {
      registerEventHandler(layerId, 'click', onClick);
      registerEventHandler(activeLayerId, 'click', onClick);
    }

    return () => {
      if (!isMobile) {
        if (onMouseMove) {
          unregisterEventHandler(layerId, 'mousemove', onMouseMove);
          unregisterEventHandler(activeLayerId, 'mousemove', onMouseMove);
        }
        if (onMouseLeave) {
          unregisterEventHandler(layerId, 'mouseleave', onMouseLeave);
          unregisterEventHandler(activeLayerId, 'mouseleave', onMouseLeave);
        }
      }
      if (onClick) {
        unregisterEventHandler(layerId, 'click', onClick);
        unregisterEventHandler(activeLayerId, 'click', onClick);
      }
    };
  }, [layerId, activeLayerId, onMouseMove, onMouseLeave, onClick, registerEventHandler, unregisterEventHandler, isMobile]);

  const iconImageXsMatch: ExpressionSpecification = useMemo(() => {
    const matchEntries = WAYPOINT_CATEGORY_UIDS.flatMap(uid => [uid, getWaypointImageName(uid, 'xs', variant)]);
    return ['match', ['get', waypointProperties.categoryUid], ...matchEntries, getWaypointImageName(DEFAULT_WAYPOINT_CATEGORY_UID, 'xs', variant)];
  }, [variant, waypointProperties.categoryUid]);

  const iconImageMdMatch: ExpressionSpecification = useMemo(() => {
    const matchEntries = WAYPOINT_CATEGORY_UIDS.flatMap(uid => [uid, getWaypointImageName(uid, 'md', variant)]);
    return ['match', ['get', waypointProperties.categoryUid], ...matchEntries, getWaypointImageName(DEFAULT_WAYPOINT_CATEGORY_UID, 'md', variant)];
  }, [variant, waypointProperties.categoryUid]);

  const activeFeatureStateExpression: ExpressionSpecification = ['boolean', ['feature-state', 'active'], false];

  // Extract the string sourceLayer (or undefined for GeoJSON) so the setFeatureState effect below
  // depends on stable primitives instead of the `source` object literal (which callers rebuild on
  // every render, causing the effect to fire redundantly).
  const sourceLayer = source.type === 'vector' ? source.sourceLayer : undefined;

  // Sync `activeWaypointId` -> mapbox feature-state. Track the previous id in a ref so we can clear
  // its state when the active id changes (avoids stale `active: true` on the previously-active feature).
  const previousActiveIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!map) {
      return;
    }
    const featureStateBase = sourceLayer !== undefined ? { source: sourceId, sourceLayer } : { source: sourceId };
    const previousActiveId = previousActiveIdRef.current;
    if (previousActiveId !== undefined && previousActiveId !== activeWaypointId) {
      map.setFeatureState({ ...featureStateBase, id: previousActiveId }, { active: false });
    }
    if (activeWaypointId !== undefined) {
      map.setFeatureState({ ...featureStateBase, id: activeWaypointId }, { active: true });
    }
    previousActiveIdRef.current = activeWaypointId;
  }, [map, sourceId, sourceLayer, activeWaypointId]);

  const markerOverlaySvg = useMemo(() => {
    if (!waypointMarkerProperties) {
      return undefined;
    }
    const categoryUid = (waypointMarkerProperties.categoryUid ?? DEFAULT_WAYPOINT_CATEGORY_UID) as WaypointCategoryUid;
    return createCategoryWaypointPinSvg(intl, categoryUid, 'md', variant);
  }, [waypointMarkerProperties, intl, variant]);

  if (!imagesAreLoaded) {
    return null;
  }

  // set `promoteId` as each feature's stable identifier so that `setFeatureState` can target features by that id
  // Must match `waypointProperties.id` (default `'id'` for GeoJSON, e.g. `'waypoint_id'` for the community vector tiles).
  const sourceProps =
    source.type === 'geojson'
      ? { type: 'geojson' as const, data: source.data, promoteId: waypointProperties.id }
      : { type: 'vector' as const, tiles: source.tiles, promoteId: { [source.sourceLayer]: waypointProperties.id } };
  const sourceLayerProp = source.type === 'vector' ? { 'source-layer': source.sourceLayer } : undefined;

  // Base layer stays on the fast paint-only path: feature-state drives `icon-opacity` (a paint
  // property) so the active feature's xs pin is hidden (0) beneath its md overlay, with no layout
  // re-evaluation across features on hover.
  const baseIconOpacity: ExpressionSpecification = ['case', activeFeatureStateExpression, 0, 1];

  const idAsNumber: ExpressionSpecification = ['to-number', ['get', waypointProperties.id]];
  const activeCaseExpression: ExpressionSpecification = ['==', ['get', waypointProperties.id], activeWaypointId ?? -1];
  const labelSortKey: ExpressionSpecification = ['case', activeCaseExpression, -1000, ['/', idAsNumber, 1000]];
  const symbolSortKey: ExpressionSpecification = ['-', 0, ['/', idAsNumber, 1000]];

  // Overlay renders ONLY the active feature (md). Filtering to one feature — vs. all N hidden by
  // opacity — keeps hit-testing correct, since opacity-0 icons are still hit-testable.
  const activeIconFilter: FilterSpecification = filter ? ['all', filter, activeCaseExpression] : activeCaseExpression;

  const labelsFilter: FilterSpecification | undefined =
    labelVisibility === 'when-active' ? (filter ? ['all', filter, activeCaseExpression] : activeCaseExpression) : filter;

  return (
    <>
      <Source id={sourceId} {...sourceProps} {...(minZoom !== undefined ? { minzoom: minZoom } : {})}>
        <Layer
          id={layerId}
          source={sourceId}
          {...(sourceLayerProp ?? {})}
          type="symbol"
          beforeId={getLayerBeforeId(map, layerId)}
          {...(filter ? { filter } : {})}
          layout={{
            ...defaultSymbolLayerLayoutProps,
            ...defaultPOIPinLayerLayoutProps,
            'icon-allow-overlap': true,
            'icon-image': iconImageXsMatch,
            'symbol-sort-key': symbolSortKey
          }}
          paint={{
            'icon-opacity': baseIconOpacity,
            'icon-opacity-transition': { duration: 0 }
          }}
        />
        <Layer
          id={activeLayerId}
          source={sourceId}
          {...(sourceLayerProp ?? {})}
          type="symbol"
          beforeId={getLayerBeforeId(map, activeLayerId)}
          filter={activeIconFilter}
          layout={{
            ...defaultSymbolLayerLayoutProps,
            ...defaultPOIPinLayerLayoutProps,
            'icon-allow-overlap': true,
            'icon-image': iconImageMdMatch
          }}
        />
        {labelVisibility !== 'never' && (
          <Layer
            id={`${layerId}-labels`}
            source={sourceId}
            {...(sourceLayerProp ?? {})}
            type="symbol"
            {...(labelsFilter ? { filter: labelsFilter } : {})}
            layout={{
              ...defaultSymbolLayerLayoutProps,
              ...defaultPOIPinLayerLayoutProps,
              'text-field': ['get', waypointProperties.title],
              'text-offset': [0, poiPinVerticalOffset / FONT_SIZE_100],
              'text-size': FONT_SIZE_100,
              // Active label wins collision — placed first, others colliding with it get hidden.
              'symbol-sort-key': labelSortKey,
              'text-allow-overlap': false,
              'text-ignore-placement': false,
              'text-optional': true
            }}
            paint={{
              'text-halo-color': COLOR_NEUTRAL_WHITE,
              'text-halo-width': 1.5,
              'text-color': COLOR_TEXT_PRIMARY
            }}
          />
        )}
      </Source>
      {waypointMarkerProperties && markerOverlaySvg && (
        <Marker
          longitude={waypointMarkerProperties.longitude}
          latitude={waypointMarkerProperties.latitude}
          draggable={isDraggable}
          onDragEnd={onSelectedDragEnd}
          anchor="bottom"
        >
          <div
            className={styles.container}
            style={{
              width: poiPinIconWidthMd,
              height: poiPinHeightMd,
              backgroundImage: `url(data:image/svg+xml;base64,${window.btoa(markerOverlaySvg.svgString)})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              cursor: isDraggable ? 'grab' : 'pointer'
            }}
          >
            {waypointMarkerProperties.title && <div className={styles.title}>{waypointMarkerProperties.title}</div>}
          </div>
        </Marker>
      )}
    </>
  );
};

export default WaypointPinLayer;
